/**
 * TDEE reconciliation math.
 *
 * Ported from the working tally.jsx prototype. These functions must stay pure
 * so they can run in both the browser (web) and the Worker (sync log analysis,
 * alerts, exported summaries).
 *
 * The "trust this" estimate is #3 below: weight-trend TDEE. Reason: it's
 * derived from measured reality (intake + scale drift) rather than a model.
 * Caveat surfaced by the UI: unreliable in the first ~2 weeks of a new diet
 * because of water/glycogen shifts.
 */

import type { DailyEntry, Profile, Reconciliation, TdeeEstimate } from './types.js';

// --- BMR formulas ---

export function mifflinBMR(opts: { weight_kg: number; height_cm: number; age: number; sex: 'male' | 'female' }): number {
  const { weight_kg, height_cm, age, sex } = opts;
  const base = 10 * weight_kg + 6.25 * height_cm - 5 * age;
  return Math.round(base + (sex === 'male' ? 5 : -161));
}

export function katchBMR(opts: { weight_kg: number; body_fat_pct: number }): number {
  const leanMass = opts.weight_kg * (1 - opts.body_fat_pct / 100);
  return Math.round(370 + 21.6 * leanMass);
}

// --- Trend utilities (simple linear regression over last N days) ---

function nonNull<T>(xs: (T | null | undefined)[]): T[] {
  return xs.filter((x): x is T => x != null);
}

export function trailingAvg<K extends keyof DailyEntry>(
  entries: DailyEntry[],
  key: K,
  n: number,
): number | null {
  const slice = nonNull(entries.slice(-n).map((e) => e[key] as number | null));
  if (!slice.length) return null;
  return slice.reduce((a, b) => a + b, 0) / slice.length;
}

/** Slope in units of `key` per day, fit by OLS across the last N populated days. */
export function trendSlope<K extends keyof DailyEntry>(
  entries: DailyEntry[],
  key: K,
  n: number,
): number {
  const slice = entries.slice(-n).filter((e) => e[key] != null);
  if (slice.length < 2) return 0;
  const ys = slice.map((e) => e[key] as number);
  const xs = slice.map((_, i) => i);
  const xMean = xs.reduce((a, b) => a + b, 0) / xs.length;
  const yMean = ys.reduce((a, b) => a + b, 0) / ys.length;
  let num = 0;
  let den = 0;
  for (let i = 0; i < xs.length; i++) {
    num += (xs[i] - xMean) * (ys[i] - yMean);
    den += (xs[i] - xMean) ** 2;
  }
  return den ? num / den : 0;
}

// --- TDEE estimators ---

/** 7700 kcal per kg of bodyweight is the textbook conversion. Some of that is water, hence the 2-week caveat. */
export const KCAL_PER_KG = 7700;

/** Cheap step→kcal heuristic: ~0.0005 kcal per step per kg body mass. Good enough as an activity estimate. */
export function stepKcal(steps: number, weight_kg: number): number {
  return Math.round(steps * weight_kg * 0.0005);
}

/** Weight-trend TDEE: requires ≥14 days of intake and weight data. */
export function tdeeFromTrend(entries: DailyEntry[], windowDays = 14): number | null {
  const populated = entries.filter((e) => e.weight_kg != null);
  if (populated.length < 7) return null;
  const slopePerDay = trendSlope(populated, 'weight_kg', Math.min(windowDays, populated.length));
  const intakeEntries = entries.filter((e) => e.calories_in != null);
  const avgIntake = trailingAvg(intakeEntries, 'calories_in', windowDays);
  if (avgIntake == null) return null;
  const deficitPerDay = -slopePerDay * KCAL_PER_KG;
  return Math.round(avgIntake + deficitPerDay);
}

/** Total kcal from any workouts logged to today's entry. Worker-side pass sums per day. */
export function workoutKcalForDay(workouts: { date: string; kcal: number | null }[], date: string): number {
  return workouts.filter((w) => w.date === date).reduce((sum, w) => sum + (w.kcal ?? 0), 0);
}

/** NEAT baseline (non-exercise activity on top of BMR + steps). Rough constant; tunable later. */
export const NEAT_BASELINE_KCAL = 200;

// --- Full reconciliation ---

export interface ReconcileInput {
  today: DailyEntry;
  history: DailyEntry[];        // ascending, INCLUDES today
  profile: Profile;
  workoutKcalToday: number;     // sum of today's logged workouts
  spreadFlagThreshold?: number; // default 500
}

export function reconcile(input: ReconcileInput): Reconciliation {
  const { today, history, profile, workoutKcalToday, spreadFlagThreshold = 500 } = input;

  const hasWeight = today.weight_kg != null;
  const hasBF = today.body_fat_pct != null;
  const hasSteps = today.steps != null;

  const mifflin = hasWeight
    ? mifflinBMR({
        weight_kg: today.weight_kg as number,
        height_cm: profile.height_cm,
        age: profile.age,
        sex: profile.sex,
      })
    : 0;

  const katch = hasWeight && hasBF
    ? katchBMR({ weight_kg: today.weight_kg as number, body_fat_pct: today.body_fat_pct as number })
    : null;

  const stepCalories = hasWeight && hasSteps
    ? stepKcal(today.steps as number, today.weight_kg as number)
    : 0;

  const activityCalories = stepCalories + workoutKcalToday + NEAT_BASELINE_KCAL;

  const mifflinTDEE: number | null = hasWeight ? mifflin + activityCalories : null;
  const katchTDEE: number | null = katch != null ? katch + activityCalories : null;
  const trendTDEE = tdeeFromTrend(history);
  const whoopTDEE = today.whoop_calories;

  const estimates: TdeeEstimate[] = [
    { label: 'Mifflin-St Jeor + activity', value: mifflinTDEE, method: 'formula' },
    { label: 'Katch-McArdle + activity', value: katchTDEE, method: 'formula (uses BF%)' },
    { label: 'Weight-trend reality', value: trendTDEE, method: '14d intake vs weight change', trust: true },
    { label: 'WHOOP', value: whoopTDEE, method: 'HR-based' },
  ].filter((e) => e.value != null);

  const values = estimates.map((e) => e.value as number);
  const min = values.length ? Math.min(...values) : null;
  const max = values.length ? Math.max(...values) : null;
  const spread = min != null && max != null ? max - min : null;
  const sorted = [...values].sort((a, b) => a - b);
  const median = sorted.length ? sorted[Math.floor(sorted.length / 2)] : null;
  const flag = spread != null && spread > spreadFlagThreshold;

  return {
    mifflin_bmr: mifflin,
    katch_bmr: katch,
    estimates,
    min,
    max,
    median,
    spread,
    flag,
    trend_tdee: trendTDEE,
  };
}

/** Convenience: intake minus whichever TDEE we trust most. */
export function computeDeficit(reconciliation: Reconciliation, intake: number | null): {
  realTDEE: number | null;
  intake: number | null;
  delta: number | null;
} {
  const realTDEE = reconciliation.trend_tdee ?? reconciliation.median;
  if (intake == null || realTDEE == null) {
    return { realTDEE, intake, delta: null };
  }
  return { realTDEE, intake, delta: intake - realTDEE };
}
